/**
 * Browser notification opt-in, in one place.
 *
 * Two surfaces ask the same question: the banner in the layout and the "Turn on notifications"
 * row in Home's set-up list. Both need the same four-way state and the same enable action, and
 * a second copy of the permission dance is how the two drift apart, so the logic lives here and
 * both call it.
 *
 * Granting does three things, in this order: take the browser permission, tell
 * `usePushSubscription` to register right now rather than waiting for a focus event, and write
 * the preference that keeps it registered. The preference write also seeds the row on a first
 * write, so it must not be skipped when permission was already granted.
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotificationPreferences, useUpdateNotificationPreferences } from '@/api/hooks';

export type PushOptInStatus = 'unsupported' | 'default' | 'granted' | 'denied';

export interface PushOptIn {
  /** `unsupported` when the browser has no Notification API at all. */
  status: PushOptInStatus;
  /** The preference read has not settled, so no surface should decide yet. */
  isLoading: boolean;
  /** A request is in flight. */
  busy: boolean;
  /** Ask the browser, then register and persist. Resolves with the outcome. */
  enable: () => Promise<PushOptInStatus>;
}

function readPermission(): PushOptInStatus {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PushOptInStatus;
}

export function usePushOptIn(): PushOptIn {
  const queryClient = useQueryClient();
  const { isLoading } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();
  const [status, setStatus] = useState<PushOptInStatus>(readPermission);
  const [busy, setBusy] = useState(false);

  // Re-read on focus: the user may have changed it in browser settings or another tab.
  useEffect(() => {
    if (status === 'unsupported') return;
    const refresh = () => setStatus(readPermission());
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [status]);

  const enable = useCallback(async (): Promise<PushOptInStatus> => {
    if (busy || typeof Notification === 'undefined') return readPermission();
    setBusy(true);
    try {
      const result = (await Notification.requestPermission()) as PushOptInStatus;
      setStatus(result);
      if (result === 'granted') {
        // Wake usePushSubscription now instead of on the next focus event.
        window.dispatchEvent(new Event('alfie:permission-change'));
        // On a first write the client seeds email_enabled: true, so creating the row cannot
        // silently opt the user out of mention emails (see upsertNotificationPreferences).
        updatePrefs.mutate({ desktop_push_enabled: true, mobile_push_enabled: true });
        await queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
      }
      return result;
    } finally {
      setBusy(false);
    }
  }, [busy, queryClient, updatePrefs]);

  return { status, isLoading, busy, enable };
}
